import React, { useEffect, useState } from 'react';
import { useParams, Link, Navigate } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import NavigationBar from '../../components/ui/NavigationBar';
import { StickyFooter } from '../../components/ui/sticky-footer';
import Icon from '../../components/AppIcon';
import { getPostBySlug, estimateReadingTime } from '../../utils/blogHelpers';

const formatDate = (value) => {
  if (!value) return '';
  const date = typeof value?.toDate === 'function' ? value.toDate() : new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  return date.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
};

const BlogArticle = () => {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
    let isMounted = true;
    setLoading(true);
    setNotFound(false);

    (async () => {
      const { post: fetched } = await getPostBySlug(slug);
      if (!isMounted) return;
      if (!fetched) {
        setNotFound(true);
      } else {
        setPost(fetched);
      }
      setLoading(false);
    })();

    return () => {
      isMounted = false;
    };
  }, [slug]);

  if (notFound) {
    return <Navigate to="/blog" replace />;
  }

  return (
    <>
      <Helmet>
        <title>{post ? `${post.title} | Nesolearn Blog` : 'Nesolearn Blog'}</title>
        <meta name="description" content={post?.excerpt || ''} />
        <meta property="og:title" content={post?.title || 'Nesolearn Blog'} />
        <meta property="og:description" content={post?.excerpt || ''} />
        {post?.coverImageUrl && <meta property="og:image" content={post.coverImageUrl} />}
        <meta property="og:type" content="article" />
        <link rel="canonical" href={`/blog/${slug}`} />
      </Helmet>

      <div className="min-h-screen w-full overflow-x-hidden bg-white">
        <NavigationBar themed />

        <main className="pt-28 pb-16">
          {loading ? (
            <div className="max-w-3xl mx-auto px-4 py-16 text-muted-foreground text-sm">
              Loading article…
            </div>
          ) : post ? (
            <article className="max-w-3xl mx-auto px-4">
              <Link
                to="/blog"
                className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-smooth mb-6"
              >
                <Icon name="ArrowLeft" size={14} />
                Back to blog
              </Link>

              {post.category && (
                <p className="text-xs font-semibold uppercase tracking-wide text-accent mb-2">
                  {post.category}
                </p>
              )}

              <h1 className="text-3xl sm:text-4xl font-bold tracking-tight text-foreground mb-4">
                {post.title}
              </h1>

              <div className="flex items-center gap-2 text-sm text-muted-foreground mb-8">
                {post.author?.avatarUrl && (
                  <img
                    src={post.author.avatarUrl}
                    alt={post.author?.name || 'Author'}
                    className="w-8 h-8 rounded-full object-cover border border-border"
                  />
                )}
                <span>{post.author?.name || 'Nesolearn Team'}</span>
                {post.author?.role && (
                  <>
                    <span aria-hidden="true">·</span>
                    <span>{post.author.role}</span>
                  </>
                )}
                <span aria-hidden="true">·</span>
                <span>{formatDate(post.publishedAt)}</span>
                {post.contentBody && (
                  <>
                    <span aria-hidden="true">·</span>
                    <span>{estimateReadingTime(post.contentBody)}</span>
                  </>
                )}
              </div>

              {post.coverImageUrl && (
                <img
                  src={post.coverImageUrl}
                  alt={post.title}
                  className="w-full rounded-xl object-cover aspect-video mb-8"
                />
              )}

              {post.type === 'quote' ? (
                <blockquote className="border-l-4 border-accent pl-6 py-2 text-xl italic text-foreground">
                  {post.quoteText}
                </blockquote>
              ) : (
                <div
                  className="prose prose-neutral max-w-none prose-headings:font-semibold prose-a:text-accent"
                  dangerouslySetInnerHTML={{ __html: post.contentBody || '' }}
                />
              )}

              {post.sourceUrl && (
                <div className="mt-10 pt-8 border-t border-border">
                  <a
                    href={post.sourceUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-black text-white text-sm font-medium transform transition-transform duration-300 ease-[cubic-bezier(0.34,1.56,0.64,1)] hover:scale-105"
                  >
                    Read the full article on {post.sourcePlatform || 'the original source'}
                    <Icon name="ExternalLink" size={14} />
                  </a>
                </div>
              )}
            </article>
          ) : null}
        </main>

        <StickyFooter />
      </div>
    </>
  );
};

export default BlogArticle;